import React, { useEffect } from 'react';
import { Paper, Title, Text, Group, Stack as MantineStack, Button, Box, Image } from '@mantine/core';
import { IconArrowLeft } from '@tabler/icons-react';
import { Game, Stack, EntryType } from '../types/models';

interface StackViewProps {
  game: Game;
  stack: Stack;
  onBack?: () => void;
  showPlayerInfo?: boolean;
}

const HANDWRITING_FONTS = [
  'Permanent Marker',
  'Caveat',
  'Shadows Into Light',
  'Indie Flower',
  'Gloria Hallelujah',
  'Amatic SC',
  'Rock Salt',
  'Nothing You Could Do',
  'Covered By Your Grace',
  'Just Another Hand',
  'La Belle Aurore',
  'Cedarville Cursive',
  'Waiting for the Sunrise',
];

// Google Fonts URL for loading all handwriting fonts
const GOOGLE_FONTS_URL = `https://fonts.googleapis.com/css2?${HANDWRITING_FONTS.map(
  font => `family=${font.replace(/ /g, '+')}:wght@400`
).join('&')}&display=swap`;

const StackView: React.FC<StackViewProps> = ({ game, stack, onBack, showPlayerInfo = false }) => {
  const originPlayer = game.players.find(p => p.id === stack.originPlayerId);
  const completedCount = stack.expectedEntries.filter(e => e.completed).length;
  const progress = (completedCount / stack.expectedEntries.length) * 100;

  // Load Google Fonts
  useEffect(() => {
    const link = document.createElement('link');
    link.href = GOOGLE_FONTS_URL;
    link.rel = 'stylesheet';
    document.head.appendChild(link);
    
    return () => {
      // Don't remove the link as other components might need it
    };
  }, []);

  // Helper to get action text
  const getActionText = (entryType: EntryType): string => {
    return entryType === EntryType.DRAWING ? 'drew' : 'wrote';
  };

  return (
    <Box py="xl" px="md">
      <MantineStack gap="xl" maw={800} mx="auto" style={{ width: '100%' }}>
          {/* Header */}
          <Group justify="space-between" align="center">
            {onBack && (
              <Button 
                variant="light" 
                leftSection={<IconArrowLeft size={16} />}
                onClick={onBack}
              >
                Back to game
              </Button>
            )}
            {!showPlayerInfo && (
              <Title order={2} ta="center" style={{ flex: 1 }}>
                {originPlayer?.name}'s stack
              </Title>
            )}
          </Group>

          {/* Evolution Flow */}
          <MantineStack gap="lg">
            {stack.entries
              .sort((a, b) => a.orderIndex - b.orderIndex) // Sort by order
              .map((entry, index) => {
              const player = game.players.find(p => p.id === entry.playerId);
              
              return (
                <Box key={entry.id}>
                  {/* Attribution */}
                  <Text size="xl" fw={600} mb="sm" ta="center">
                    {player?.name || 'Unknown'} {getActionText(entry.entryType)}:
                  </Text>

                  {/* Content */}
                  <Box
                    style={{
                      width: '100%',
                      maxWidth: '400px',
                      height: '400px',
                      margin: '0 auto',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: entry.entryType === EntryType.TEXT ? 'white' : 'transparent',
                      borderRadius: '8px',
                      boxShadow: entry.entryType === EntryType.TEXT ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                      overflow: 'hidden'
                    }}
                  >
                    {entry.entryType === EntryType.DRAWING ? (
                      <img 
                        src={entry.content} 
                        alt={`Drawing by ${player?.name}`}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain'
                        }}
                      />
                    ) : (
                      <Text 
                        ta="center" 
                        style={{ 
                          fontFamily: player?.handwritingFont ? `"${player.handwritingFont}", var(--mantine-font-family)` : 'var(--mantine-font-family)',
                          fontSize: '28px',
                          color: '#333',
                          padding: '20px',
                          wordWrap: 'break-word',
                          overflowWrap: 'break-word',
                          hyphens: 'auto',
                          lineHeight: 1.3,
                          width: '100%',
                          maxWidth: '360px'
                        }}
                      >
                        {entry.content}
                      </Text>
                    )}
                  </Box>

                  {/* Arrow between entries (except last) */}
                  {index < stack.entries.length - 1 && (
                    <Group justify="center" my="xl">
                      <Text 
                        style={{ 
                          fontSize: '48px',
                          color: '#0da388',
                          fontWeight: 'bold'
                        }}
                      >
                        ↓
                      </Text>
                    </Group>
                  )}
                </Box>
              );
            })}
          </MantineStack>

      </MantineStack>
    </Box>
  );
};

export default StackView; 